import React, {useContext, ChangeEvent} from 'react';
import {Button, message} from 'antd';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@apollo/client';
import dayjs from 'dayjs';
import {DeliveryTaskFilter, DeliveryTaskStatus, Driver} from '../types/DeliveryTaskType';
import {GET_AREAS_BY_WAREHOUSE_ID} from '../../../../graphql/queries/getAreasByWarehouseId';
import {useAllDspsForWmsPermission} from '../../accounts/graphql/queries/getAllDspsForWmsPermission';
import {WarehouseContext} from '../../../../contexts/WarehouseContext';

import DateTimeRangeFilterItem from '../../components/DateTimeRangeFilterItem';
import BatchInputFilterItem from '../../components/BatchInputFilterItem';
import ParcelFilterStyle from '../../components/ParcelFilterStyle';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';


interface DeliveryTaskFiltersProps {
  filters: DeliveryTaskFilter;
  onFilterChange: (filters: Partial<DeliveryTaskFilter>) => void;
  onReset: () => void;
  onApplyFilter: () => void;
  drivers: Driver[];
  mode?: 'wms' | 'dsp';
}

const DeliveryTaskFilters: React.FC<DeliveryTaskFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
  onApplyFilter,
  drivers,
  mode,
}) => {
  const { t } = useTranslation();
  // 统一使用 WarehouseContext 的所选仓（各 Layout 已从 URL/localStorage 注入），不再回退账号绑定仓
  const warehouseContext = useContext(WarehouseContext);
  const warehouse = warehouseContext?.currentWarehouse ?? null;
  const isDsp = mode === 'dsp';

  // 统一提取 warehouseId
  const warehouseId = React.useMemo(() => {
    if (!warehouse?.id) return undefined;
    return typeof warehouse.id === 'string' ? warehouse.id : String(warehouse.id);
  }, [warehouse?.id]);

  // 获取区域数据
  const { data: areasData, loading: areasLoading } = useQuery(GET_AREAS_BY_WAREHOUSE_ID, {
    variables: { warehouseId: warehouseId ? Number(warehouseId) : undefined },
    skip: !warehouseId
  });

  // 获取DSP数据（DSP 经理不需要，因为他们已经有固定的 DSP）
  // 确保 warehouseId 正确传递：统一转换为字符串
  const warehouseIdForDspQuery = React.useMemo(() => {
    if (!warehouseId) {
      return undefined;
    }
    return warehouseId;
  }, [warehouseId]);

  const { allDspsForWmsPermission: dspsData, loading: dspsLoading } = useAllDspsForWmsPermission(
    warehouseIdForDspQuery,
    isDsp // DSP 经理跳过查询
  );

  // 最大日期范围：30天（一个月）
  const MAX_DATE_RANGE_DAYS = 30;

  const handleTaskDateChange = (dates: [string | null, string | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      onFilterChange({
        ...filters,
        taskDateStart: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : undefined,
        taskDateEnd: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : undefined
      });
      return;
    }

    const startDate = dayjs(dates[0]);
    const endDate = dayjs(dates[1]);
    const daysDiff = endDate.diff(startDate, 'day');

    // 如果日期范围超过一个月，自动调整结束日期
    if (daysDiff > MAX_DATE_RANGE_DAYS) {
      const adjustedEndDate = startDate.add(MAX_DATE_RANGE_DAYS, 'day');
      message.warning(t('delivery.tasks.message.dateRangeExceeded', { days: MAX_DATE_RANGE_DAYS }));
      onFilterChange({
        ...filters,
        taskDateStart: startDate.format('YYYY-MM-DD'),
        taskDateEnd: adjustedEndDate.format('YYYY-MM-DD')
      });
    } else {
      onFilterChange({
        ...filters,
        taskDateStart: startDate.format('YYYY-MM-DD'),
        taskDateEnd: endDate.format('YYYY-MM-DD')
      });
    }
  };

  const handlePublishDateChange = (dates: [string | null, string | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      onFilterChange({
        ...filters,
        publishDate: undefined
      });
      return;
    }

    const startDate = dayjs(dates[0]);
    const endDate = dayjs(dates[1]);
    const daysDiff = endDate.diff(startDate, 'day');

    // 如果日期范围超过一个月，自动调整结束日期
    if (daysDiff > MAX_DATE_RANGE_DAYS) {
      const adjustedEndDate = startDate.add(MAX_DATE_RANGE_DAYS, 'day');
      message.warning(t('delivery.tasks.message.dateRangeExceeded', { days: MAX_DATE_RANGE_DAYS }));
      onFilterChange({
        ...filters,
        publishDate: {
          start: startDate.toISOString(),
          end: adjustedEndDate.toISOString()
        }
      });
    } else {
      onFilterChange({
        ...filters,
        publishDate: {
          start: startDate.toISOString(),
          end: endDate.toISOString()
        }
      });
    }
  };

  const handleAreaChange = (value: string) => {
    onFilterChange({ ...filters, areaId: value ? Number(value) : undefined });
  };

  const handleStatusChange = (values: any) => {
    const statusValues = Array.isArray(values) ? values : (values ? [values] : []);
    onFilterChange({ 
      ...filters, 
      status: statusValues.length > 0 ? statusValues.map(v => v as DeliveryTaskStatus) : undefined 
    });
  };

  const handleDspChange = (value: string) => {
    const newDspId = value ? Number(value) : undefined;
    
    // DSP 变化时，清空已选的司机（因为司机列表会重新查询）
    onFilterChange({ 
      ...filters, 
      dspId: newDspId,
      driverIds: undefined
    });
  };

  const handleDriverChange = (values: any) => {
    const driverValues = Array.isArray(values) ? values.map(v => Number(v)) : (values ? [Number(values)] : []);
    onFilterChange({ ...filters, driverIds: driverValues.length > 0 ? driverValues : undefined });
  };

  // 根据 filters.dspId 过滤司机列表（如果用户选择了 DSP）
  const filteredDrivers = React.useMemo(() => {
    if (filters.dspId) {
      // 如果选择了 DSP，只显示该 DSP 的司机
      return drivers.filter(driver => driver.dspId == filters.dspId);
    }
    // 如果没有选择 DSP，显示所有司机（用于筛选任务）
    return drivers;
  }, [drivers, filters.dspId]);

  const handleFilter = () => {
    onApplyFilter();
  };

  const handleReset = () => {
    onReset();
  };

  const labelTaskId = String(t('delivery.tasks.label.taskId'));
  const labelTaskDate = String(t('delivery.tasks.label.taskDate'));
  const labelPublishDate = String(t('delivery.tasks.label.publishDate'));
  const labelArea = String(t('delivery.tasks.label.area'));
  const labelStatus = String(t('delivery.tasks.label.status'));
  const labelDsp = String(t('wms.review.filters.dsp'));
  const labelDriver = String(t('delivery.tasks.label.driver'));

  // Task ID 虽然独占一行，但它属于第一列，需要纳入第一列宽度计算以保证与“任务日期”等第一列筛选项对齐
  const col1Labels = [labelTaskId, labelTaskDate, labelStatus];
  const col2Labels = [labelPublishDate, labelDsp];
  const col3Labels = [labelArea, labelDriver];

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="mb-4">
        <BatchInputFilterItem
          label={labelTaskId}
          value={filters.taskIds}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onFilterChange({
            ...filters,
            taskIds: e.target.value || undefined
          })}
          spanClassName={getSmartLabelClassName(col1Labels)}
          placeholder={t('delivery.tasks.placeholder.taskIds') || 'Enter multiple task IDs, separated by line, comma or semicolon'}
          rows={2}
        />
      </div>

      {/* 使用 Grid 布局确保列对齐 */}
      <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
        <div className="min-w-0">
          <DateTimeRangeFilterItem
            label={labelTaskDate}
            value={filters.taskDateStart || filters.taskDateEnd ? [
              filters.taskDateStart || null,
              filters.taskDateEnd || null
            ] : null}
            onChange={handleTaskDateChange}
            format="YYYY-MM-DD"
            showTime={false}
            allowClear={false}
            spanClassName={getSmartLabelClassName(col1Labels)}
          />
        </div>
        <div className="min-w-0">
          <DateTimeRangeFilterItem
            label={labelPublishDate}
            value={filters.publishDate ? [
              filters.publishDate.start,
              filters.publishDate.end
            ] : null}
            onChange={handlePublishDateChange}
            format="YYYY-MM-DD HH:mm"
            showTime={{
              format: 'HH:mm',
              defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')]
            }}
            spanClassName={getSmartLabelClassName(col2Labels)}
          />
        </div>
        <div className="min-w-0">
          <ParcelFilterStyle
            label={labelArea}
            value={filters.areaId ? String(filters.areaId) : undefined}
            onChange={handleAreaChange}
            options={areasData?.areaList?.map((area: any) => ({
              label: area.name,
              value: area.id
            })) || []}
            placeholder={t('components.input.all') || undefined}
            showSearch
            disabled={areasLoading}
            spanClassName={getSmartLabelClassName(col3Labels)}
          />
        </div>
        {/* 第二行 */}
        <div className="min-w-0">
          <ParcelFilterStyle
            label={labelStatus}
            value={filters.status ? filters.status.map(s => String(s)) : []}
            onChange={handleStatusChange}
            options={[
              { label: t('delivery.tasks.status.CREATED'), value: String(DeliveryTaskStatus.CREATED) },
              { label: t('delivery.tasks.status.DRIVER_PICKINGUP'), value: String(DeliveryTaskStatus.DRIVER_PICKINGUP) },
              { label: t('delivery.tasks.status.DELIVERING'), value: String(DeliveryTaskStatus.DELIVERING) },
              { label: t('delivery.tasks.status.COMPLETED'), value: String(DeliveryTaskStatus.COMPLETED) }
            ]}
            placeholder={t('components.input.all') || undefined}
            mode="multiple"
            spanClassName={getSmartLabelClassName(col1Labels)}
          />
        </div>
        {/* DSP 下拉框：DSP 经理不显示，因为他们已经有固定的 DSP */}
        {!isDsp ? (
          <div className="min-w-0">
            <ParcelFilterStyle
              label={labelDsp}
              value={filters.dspId ? String(filters.dspId) : undefined}
              onChange={handleDspChange}
              options={dspsData?.map((dsp: any) => ({
                label: dsp.name,
                value: dsp.id
              })) || []}
              placeholder={t('components.input.all') || undefined}
              showSearch
              disabled={dspsLoading}
              spanClassName={getSmartLabelClassName(col2Labels)}
            />
          </div>
        ) : (
          <div className="min-w-0"></div>
        )}
        <div className="min-w-0">
          <ParcelFilterStyle
            label={labelDriver}
            value={filters.driverIds ? filters.driverIds.map(id => String(id)) : []}
            onChange={handleDriverChange}
            options={filteredDrivers.map(driver => ({
              label: `${driver.phone} (${driver.name})`,
              value: String(driver.accountId)
            }))}
            placeholder={t('components.input.all') || undefined}
            mode="multiple"
            showSearch
            spanClassName={getSmartLabelClassName(col3Labels)}
          />
        </div>
      </div>

      <div className="flex justify-end gap-4 pt-3">
        <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.filter')}</span>
        </Button>
        <Button onClick={handleReset} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.reset')}</span>
        </Button>
      </div>
    </div>
  );
};

export default DeliveryTaskFilters;